import React, { useEffect, useLayoutEffect } from 'react';
// import './globals.scss';
import { useAppDispatch, useAppSelector } from './app/hooks';
import { filterServices, IMe, selectInitializing, selectMe, setInitializing, setMe, setSessionExpired, setScrHeight, setToken } from './AppSlice';
import { hot } from 'react-hot-loader'
import AppContent from './components/appContent';
import LoginForm from './components/LoginForm';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import axios from 'axios';
import SettingsPage from './components/settings/SettingsPage';
import { endpoints } from './data/endpoints';
import { clearToken, getToken, getTokenData, isTokenExpired } from './data/auth';
import { isDevelopmentMode } from './config/build';

const LOGIN_PATH = '/login';
const SESSION_EXPIRED_STORAGE_KEY = 'session_expired';
const TOKEN_CHECK_INTERVAL_MS = 15000;
const DEVELOPMENT_USER: IMe = {
  username: 'developer',
  name: 'Developer',
  isLogin: true,
  roles: ['ROLE_ADMIN'],
  permissions: ['gateway:admin'],
};

function App() {

  const dispatch = useAppDispatch();
  const initializing = useAppSelector(selectInitializing)
  const me = useAppSelector(selectMe)
  const location = useLocation();

  useLayoutEffect(() => {
    function updateSize() {
      dispatch(setScrHeight(window.innerHeight))
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, [dispatch]);

  useEffect(() => {
    if (isDevelopmentMode) {
      dispatch(setMe(DEVELOPMENT_USER));
      dispatch(filterServices());
      dispatch(setInitializing(false));
      return;
    }

    const storedToken = getToken();
    if (storedToken) {
      if (isTokenExpired(storedToken)) {
        clearToken();
        sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, 'true');
        dispatch(setMe(null));
        dispatch(setToken(null));
        dispatch(setSessionExpired(true));
      } else {
        const tokenData = getTokenData(storedToken);
        if (tokenData) {
          dispatch(setToken(storedToken));
          dispatch(setMe({
            username: tokenData.sub,
            name: tokenData.sub,
            isLogin: true,
            roles: (tokenData as any).authorities ?? [],
            permissions: (tokenData as any).permissions ?? [],
          }));
          dispatch(filterServices());
        }
      }
    }
    dispatch(setInitializing(false));
  }, [dispatch]);

  useEffect(() => {
    if (isDevelopmentMode) return;

    const checkTokenExpiry = () => {
      const storedToken = getToken();
      if (storedToken && isTokenExpired(storedToken)) {
        clearToken();
        sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, 'true');
        dispatch(setMe(null));
        dispatch(setToken(null));
        dispatch(setSessionExpired(true));
      }
    };

    const intervalId = setInterval(checkTokenExpiry, TOKEN_CHECK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [dispatch]);

  useEffect(() => {
    if (isDevelopmentMode) return;

    let handlingExpiry = false;

    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        const isUnauthorized = error?.response?.status === 401;
        const requestUrl = error?.config?.url ?? '';
        const isLoginRequest = requestUrl.includes(endpoints.auth.login);

        if (isUnauthorized && !isLoginRequest && !handlingExpiry) {
          handlingExpiry = true;
          clearToken();
          sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, 'true');
          dispatch(setMe(null));
          dispatch(setToken(null));
          dispatch(setSessionExpired(true));
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [dispatch]);

  if (initializing) return <div className="div-center">Initializing...</div>

  const isAuthenticated = isDevelopmentMode || me?.isLogin;
  const protectedElement = (element: React.ReactElement) =>
    isAuthenticated
      ? element
      : <Navigate to={LOGIN_PATH} state={{ from: location.pathname + location.search }} replace />;

  return (
    <Routes>
      <Route path={LOGIN_PATH} element={isAuthenticated ? <Navigate to="/" replace /> : <LoginForm />} />
      <Route path="/" element={protectedElement(<AppContent />)} />
      <Route path="/service/:serviceId" element={protectedElement(<AppContent />)} />
      <Route path="/settings" element={protectedElement(<SettingsPage />)} />
      <Route path="/settings/service/:serviceId" element={protectedElement(<SettingsPage />)} />
      <Route path="/settings/:tab" element={protectedElement(<SettingsPage />)} />
      <Route path="*" element={<Navigate to={isAuthenticated ? "/" : LOGIN_PATH} replace />} />
    </Routes>
  );
}

export default hot(module)(App)
